// src/components/admin/QRGenerator.jsx
import { useState, useEffect } from "react";
import QRCode from "qrcode";
import { saveAs } from "file-saver";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  onSnapshot,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { addLog } from "../../utils/audit";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../services/firebase";

export default function QRGenerator() {
  const [assetData, setAssetData] = useState({
    name: "",
    description: "",
    serialNumber: "",
    category: "",
    building: "",
    room: "",
    receiveDate: "",
    cost: "",
    salvageValue: "",
  });
  const [generatedQR, setGeneratedQR] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCreatingAsset, setIsCreatingAsset] = useState(false);
  const [createdAsset, setCreatedAsset] = useState(null);
  const [user] = useAuthState(auth);

  // Dropdown data
  const [buildings, setBuildings] = useState([]);
  const [rooms, setRooms] = useState([]);

  const categoryOptions = [
    { value: "ict-equipment", label: "ICT Equipment" },
    { value: "devices", label: "Devices" },
    { value: "laboratory-equipment", label: "Laboratory Equipment" },
  ];

  // 🔹 Fetch rooms and buildings from Firestore
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "rooms"), (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRooms(data);

      // extract unique building names
      const uniqueBuildings = [...new Set(data.map((r) => r.building))];
      setBuildings(uniqueBuildings);
    });
    return () => unsub();
  }, []);

  // 🔹 Upload QR to Cloudinary
  const uploadToCloudinary = async (dataUrl) => {
    const formData = new FormData();
    formData.append("file", dataUrl);
    formData.append("upload_preset", "caths_unsigned");
    formData.append("folder", "asset_qr_codes");

    const res = await fetch(
      "https://api.cloudinary.com/v1_1/dse3b23i2/image/upload",
      {
        method: "POST",
        body: formData,
      }
    );
    const data = await res.json();
    if (!data.secure_url)
      throw new Error(data.error?.message || "Cloudinary upload failed");
    return data.secure_url;
  };

  // 🔹 Create Asset
  const createAsset = async () => {
    if (!assetData.name.trim()) return alert("Please enter asset name");
    if (!assetData.category) return alert("Please select a category");
    if (!assetData.serialNumber.trim()) return alert("Please enter serial number");
    if (!/^[a-zA-Z0-9]+$/.test(assetData.serialNumber.trim())) return alert("Serial number must contain only alphanumeric characters (letters and numbers).");
    if (!assetData.building) return alert("Please select a building");
    if (!assetData.room) return alert("Please select a room");
    if (!assetData.receiveDate) return alert("Please select the receive date");
    if (!assetData.cost || assetData.cost <= 0) return alert("Please enter a valid cost");
    if (!assetData.salvageValue || assetData.salvageValue < 0) return alert("Please enter a valid salvage value");
    if (parseFloat(assetData.cost) <= parseFloat(assetData.salvageValue)) return alert("Cost must be greater than salvage value");

    // Check for unique serial number
    const q = query(collection(db, "assets"), where("serialNumber", "==", assetData.serialNumber.trim()));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      return alert("❌ Serial number already exists. Please enter a unique serial number.");
    }

    const cost = parseFloat(assetData.cost);
    const salvageValue = parseFloat(assetData.salvageValue);
    const usefulLife = 5;
    const annualDepreciation = (cost - salvageValue) / usefulLife;

    const receiveDateObj = new Date(assetData.receiveDate);
    const depreciationDateObj = new Date(receiveDateObj);
    depreciationDateObj.setFullYear(receiveDateObj.getFullYear() + usefulLife);

    setIsCreatingAsset(true);
    try {
      const docRef = await addDoc(collection(db, "assets"), {
        name: assetData.name.trim(),
        description: assetData.description.trim(),
        serialNumber: assetData.serialNumber.trim(),
        category: assetData.category,
        categoryLabel:
          categoryOptions.find((cat) => cat.value === assetData.category)
            ?.label || assetData.category,
        building: assetData.building,
        room: assetData.room,
        location: assetData.room,
        status: "available",
        receiveDate: assetData.receiveDate,
        depreciationDate: depreciationDateObj.toISOString().split("T")[0],
        cost: cost,
        salvageValue: salvageValue,
        usefulLife: usefulLife,
        annualDepreciation: annualDepreciation,
        createdAt: serverTimestamp(),
        createdBy: user?.uid,
      });

      const newAsset = {
        id: docRef.id,
        ...assetData,
        status: "available",
        depreciationDate: depreciationDateObj.toISOString().split("T")[0],
        cost: cost,
        salvageValue: salvageValue,
        usefulLife: usefulLife,
        annualDepreciation: annualDepreciation,
      };
      setCreatedAsset(newAsset);

      await addLog(
        user?.uid || "system",
        `Created asset: ${assetData.name} with straight-line depreciation ₱${annualDepreciation.toFixed(2)}/year`,
        {
          assetId: docRef.id,
          assetName: assetData.name,
          category: assetData.category,
          cost: cost,
          salvageValue: salvageValue,
          usefulLife: usefulLife,
          annualDepreciation: annualDepreciation,
          location: assetData.room
        }
      );

      return newAsset;
    } catch (err) {
      console.error("Error creating asset:", err);
      alert("❌ Failed to create asset: " + err.message);
      return null;
    } finally {
      setIsCreatingAsset(false);
    }
  };

  // 🔹 Generate QR and upload
  const generateQRCode = async (asset) => {
    setIsGenerating(true);
    try {
      const appBaseURL = "https://caths-ams-mobile.web.app";
      const qrLink = `${appBaseURL}/scan?id=${asset.id}`;
      const qrOptions = {
        color: { dark: "#000000", light: "#ffffff" },
        width: 250,
        margin: 1,
        errorCorrectionLevel: "M",
      };

      const dataUrl = await QRCode.toDataURL(qrLink, qrOptions);
      const cloudinaryUrl = await uploadToCloudinary(dataUrl);

      setGeneratedQR(cloudinaryUrl);

      await updateDoc(doc(db, "assets", asset.id), {
        qrUrl: cloudinaryUrl,
        qrLink,
      });

      await addLog(
        user?.uid || "system",
        `Generated QR code for asset: ${asset.name}`,
        {
          assetId: asset.id,
          assetName: asset.name,
          qrLink: qrLink,
          qrUrl: cloudinaryUrl
        }
      );

      alert("✅ Secure QR code created and uploaded!");
    } catch (err) {
      console.error("QR generation/upload error:", err);
      alert("❌ Failed to generate QR: " + err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreateAndGenerate = async () => {
    const asset = await createAsset();
    if (asset) await generateQRCode(asset);
  };

  const downloadQRCode = () => {
    if (!generatedQR || !createdAsset) return;
    saveAs(generatedQR, `asset-${createdAsset.name}-${createdAsset.id}.png`);
  };

  const resetForm = () => {
    setAssetData({
      name: "",
      description: "",
      serialNumber: "",
      category: "",
      building: "",
      room: "",
      receiveDate: "",
      cost: "",
      salvageValue: "",
    });
    setGeneratedQR("");
    setCreatedAsset(null);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Form */}
        <div className="space-y-6">
          {/* Asset Info */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Asset Name *
            </label>
            <input
              type="text"
              value={assetData.name}
              onChange={(e) =>
                setAssetData({ ...assetData, name: e.target.value })
              }
              placeholder="Enter asset name"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={assetData.description}
              onChange={(e) =>
                setAssetData({ ...assetData, description: e.target.value })
              }
              placeholder="Enter asset description"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              rows="3"
            />
          </div>

          {/* Serial & Category */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Serial Number *
              </label>
              <input
                type="text"
                value={assetData.serialNumber}
                onChange={(e) =>
                  setAssetData({ ...assetData, serialNumber: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                value={assetData.category}
                onChange={(e) =>
                  setAssetData({ ...assetData, category: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select Category</option>
                {categoryOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Cost & Salvage Value */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cost (₱) *
              </label>
              <input
                type="number"
                value={assetData.cost}
                onChange={(e) =>
                  setAssetData({ ...assetData, cost: e.target.value })
                }
                placeholder="e.g. 60000"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Salvage Value (₱) *
              </label>
              <input
                type="number"
                value={assetData.salvageValue}
                onChange={(e) =>
                  setAssetData({ ...assetData, salvageValue: e.target.value })
                }
                placeholder="e.g. 10000"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                min="0"
              />
            </div>
          </div>

          {/* Building & Room */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Building *
              </label>
              <select
                value={assetData.building}
                onChange={(e) =>
                  setAssetData({
                    ...assetData,
                    building: e.target.value,
                    room: "",
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select Building</option>
                {buildings.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Room *
              </label>
              <select
                value={assetData.room}
                onChange={(e) =>
                  setAssetData({ ...assetData, room: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                disabled={!assetData.building}
              >
                <option value="">Select Room</option>
                {rooms
                  .filter((r) => r.building === assetData.building)
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Annual Depreciation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Annual Depreciation (Straight-Line)
            </label>
            <input
              type="text"
              value={
                assetData.cost && assetData.salvageValue && parseFloat(assetData.cost) > parseFloat(assetData.salvageValue)
                  ? `₱${((parseFloat(assetData.cost) - parseFloat(assetData.salvageValue)) / 5).toFixed(2)}`
                  : ""
              }
              disabled
              placeholder="Enter cost and salvage value to calculate"
              className="w-full px-4 py-2 border border-gray-300 bg-gray-100 rounded-lg"
            />
          </div>

          {/* Receive & Depreciation Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Receive Date *
              </label>
              <input
                type="date"
                value={assetData.receiveDate}
                onChange={(e) =>
                  setAssetData({ ...assetData, receiveDate: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Depreciation Date (Auto 5 Years)
              </label>
              <input
                type="date"
                value={
                  assetData.receiveDate
                    ? new Date(
                        new Date(assetData.receiveDate).setFullYear(
                          new Date(assetData.receiveDate).getFullYear() + 5
                        )
                      )
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                disabled
                className="w-full px-4 py-2 border border-gray-300 bg-gray-100 rounded-lg"
              />
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              onClick={handleCreateAndGenerate}
              disabled={isCreatingAsset || isGenerating}
              className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition"
            >
              {isCreatingAsset
                ? "Creating Asset..."
                : isGenerating
                ? "Generating QR..."
                : "Create Asset & Generate QR"}
            </button>
            {createdAsset && (
              <button
                onClick={resetForm}
                className="bg-gray-500 text-white px-4 py-3 rounded-lg hover:bg-gray-600"
              >
                New Asset
              </button>
            )}
          </div>
        </div>

        {/* Right Side: QR Preview */}
        <div className="flex flex-col items-center justify-center">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-4 w-full bg-gray-50 min-h-[400px] flex items-center justify-center">
            {generatedQR ? (
              <div className="text-center">
                <img
                  src={generatedQR}
                  alt="Generated QR"
                  className="max-w-full h-auto mx-auto mb-4"
                />
                {createdAsset && (
                  <div className="mt-4 p-4 bg-white rounded-lg border">
                    <h4 className="font-semibold text-gray-800">
                      {createdAsset.name}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {createdAsset.building} - {createdAsset.room}
                    </p>
                    <p className="text-sm text-gray-600">
                      Cost: ₱{createdAsset.cost?.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600">
                      Annual Depreciation: ₱{createdAsset.annualDepreciation?.toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-600">
                      Depreciation End: {createdAsset.depreciationDate}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-gray-400 text-center">
                <p>QR Code Preview</p>
                <p className="text-sm">Create an asset to generate QR</p>
              </div>
            )}
          </div>

          {generatedQR && (
            <button
              onClick={downloadQRCode}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 mt-4"
            >
              Download QR
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
